'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Script from 'next/script';
import { getVisitorId } from '@/app/utils/visitor-tracking';

type Variant = 'control' | 'treatment_a' | 'treatment_b';

interface ExperimentsContextValue {
  getVariant: (experimentName: string) => Variant;
}

const ExperimentsContext = createContext<ExperimentsContextValue | undefined>(undefined);

const WUNDERKIND_WEBSITE_ID = '8708';

// Deterministic hash for bucketing
const hashStringToInt = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // Convert to 32bit int
  }
  return Math.abs(hash);
};

const bucketToVariant = (bucket: number): Variant => {
  const mod = bucket % 3;
  if (mod === 0) return 'control';
  if (mod === 1) return 'treatment_a';
  return 'treatment_b';
};

const getGAClientId = (): string | undefined => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]*)/);
    return match?.[1] || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Extracts the last digit (0–9) from the best available persistent ID.
 * GA Client IDs end with numeric timestamp digits; visitor IDs (UUIDs) may
 * end with hex chars a–f, so we parse base-16 and mod 10 to normalise.
 * Returns -1 when no usable ID is available yet (SSR / pre-hydration).
 */
const getUserTestGroup = (visitorId: string | null): number => {
  const id = getGAClientId() || visitorId;
  if (!id || id === 'server') return -1;
  const lastChar = id.slice(-1);
  const parsed = parseInt(lastChar, 16);
  if (isNaN(parsed)) return -1;
  return parsed % 10;
};

export const ExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const [seed, setSeed] = useState<string>('server');

  useEffect(() => {
    const id = getVisitorId();
    if (id && id !== seed) {
      setSeed(id);
    }
  }, [seed]);

  const value = useMemo<ExperimentsContextValue>(() => ({
    getVariant: (experimentName: string): Variant => {
      const key = `${experimentName}:${seed}`;
      const bucket = hashStringToInt(key);
      return bucketToVariant(bucket);
    },
  }), [seed]);

  return (
    <ExperimentsContext.Provider value={value}>
      <Script
        id="wknd-smarttag"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `(function(d){var e=d.createElement('script');e.src=d.location.protocol+'//tag.wknd.ai/${WUNDERKIND_WEBSITE_ID}/i.js';e.async=true;d.getElementsByTagName("head")[0].appendChild(e);}(document));`,
        }}
      />
      {children}
    </ExperimentsContext.Provider>
  );
};

export const useExperiment = (experimentName: string): Variant => {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) throw new Error('useExperiment must be used within ExperimentsProvider');
  return ctx.getVariant(experimentName);
};


